import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Ruler } from 'lucide-react';
import { PLAN_MEASURE_APP_URL } from '@/config/site';
import { trackHpdDrawCtaClicked } from '@/lib/analytics';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/floor-plan-creator', label: 'Floor Plan Creator' },
    { path: '/measurement-tool', label: 'Plan Measure' },
    { path: '/floor-plan-service', label: 'Floor Plan Service' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/gallery', label: 'Examples' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isExternal = PLAN_MEASURE_APP_URL.startsWith('http');

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.jpg" alt="House Plan Direct" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 ml-1">
              {isExternal ? (
                <a href={PLAN_MEASURE_APP_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackHpdDrawCtaClicked('nav', 'Start HPD Draw Free', location.pathname, 'general')}>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" data-testid="header-open-plan-measure">
                    <Ruler className="mr-2 h-4 w-4" /> Start HPD Draw Free
                  </Button>
                </a>
              ) : (
                <Link to={PLAN_MEASURE_APP_URL} onClick={() => trackHpdDrawCtaClicked('nav', 'Start HPD Draw Free', location.pathname, 'general')}>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" data-testid="header-open-plan-measure">
                    <Ruler className="mr-2 h-4 w-4" /> Start HPD Draw Free
                  </Button>
                </Link>
              )}
              <Link to="/order">
                <Button size="sm" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 whitespace-nowrap" data-testid="header-order-floor-plan-btn">
                  We draw your plan
                </Button>
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 space-y-3" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-2 text-sm font-medium ${
                  isActive(link.path)
                    ? 'text-blue-600'
                    : 'text-gray-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 space-y-2">
              {isExternal ? (
                <a href={PLAN_MEASURE_APP_URL} target="_blank" rel="noopener noreferrer" onClick={() => { trackHpdDrawCtaClicked('nav', 'Start HPD Draw Free', location.pathname, 'general'); setIsMenuOpen(false); }}>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mobile-open-plan-measure">
                    <Ruler className="mr-2 h-4 w-4" /> Start HPD Draw Free
                  </Button>
                </a>
              ) : (
                <Link to={PLAN_MEASURE_APP_URL} onClick={() => { trackHpdDrawCtaClicked('nav', 'Start HPD Draw Free', location.pathname, 'general'); setIsMenuOpen(false); }}>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mobile-open-plan-measure">
                    <Ruler className="mr-2 h-4 w-4" /> Start HPD Draw Free
                  </Button>
                </Link>
              )}
              <Link to="/order" onClick={() => setIsMenuOpen(false)}>
                <Button variant="outline" className="w-full border-blue-600 text-blue-600" data-testid="mobile-order-floor-plan-btn">
                  We draw your plan
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
